// Shared policy-rule editor helpers (used by the rule create/edit wizard).
// Kept out of PolicyTab so the list view and the editor don't duplicate logic.
import type { ArgCondition, PolicyAction, PolicyRule, PolicyRuleInput, ProjectSelection, Server } from "@/lib/api";

export const OPS = ["exists", "eq", "neq", "gt", "lt", "gte", "lte", "in", "matches"] as const;

export const SELECT_CLS =
  "h-9 w-full rounded-md border border-input bg-background px-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50";

// HOLD pending-window presets (seconds). 0 = use the server default (1h).
export const TTL_PRESETS: { label: string; secs: number }[] = [
  { label: "Default (1 hour)", secs: 0 },
  { label: "6 hours", secs: 6 * 3600 },
  { label: "1 day", secs: 24 * 3600 },
  { label: "3 days", secs: 3 * 24 * 3600 },
  { label: "7 days", secs: 7 * 24 * 3600 },
  { label: "30 days", secs: 30 * 24 * 3600 },
];

// parseArgValue turns the editor's text into the typed value the engine
// expects: JSON when it parses (number / bool / array / object), else the raw
// string. For `in`, a non-JSON comma list becomes an array.
export function parseArgValue(raw: string, op: string): unknown {
  const s = raw.trim();
  if (s === "") return undefined;
  try {
    return JSON.parse(s);
  } catch {
    if (op === "in") return s.split(",").map((x) => x.trim()).filter(Boolean);
    return s;
  }
}

export function argValueToText(v: unknown): string {
  if (v === undefined || v === null) return "";
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

export interface EnabledServer {
  name: string;
  tools: string[];
}

// resolveEnabled intersects the tenant's connected servers with the agent's
// selection (null = all connected servers + all tools), mirroring the proxy's
// allowlist — so the picker offers exactly what this agent can call.
export function resolveEnabled(servers: Server[], sel: ProjectSelection | null): EnabledServer[] {
  const connected = servers.filter((s) => s.connected);
  const sortTools = (t: string[]) => [...t].sort((a, b) => a.localeCompare(b));
  if (!sel)
    return connected
      .map((s) => ({ name: s.name, tools: sortTools(s.tools) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  const byName = new Map(connected.map((s) => [s.name, s]));
  const out: EnabledServer[] = [];
  for (const sse of sel.servers) {
    const live = byName.get(sse.name);
    if (!live) continue;
    const tools = sse.all_tools ? live.tools : (sse.tools ?? []).filter((t) => live.tools.includes(t));
    out.push({ name: sse.name, tools: sortTools(tools) });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

// parseToolSel splits a stored tool matcher into the picker's two parts.
// "" → any; "srv.*" → all tools on srv; "srv.tool" → that tool.
export function parseToolSel(tool: string): { server: string; tool: string } {
  if (!tool) return { server: "", tool: "" };
  if (tool.endsWith(".*")) return { server: tool.slice(0, -2), tool: "*" };
  const dot = tool.indexOf(".");
  if (dot < 0) return { server: tool, tool: "*" };
  return { server: tool.slice(0, dot), tool: tool.slice(dot + 1) };
}

// schemaFields pulls suggestable argument paths (name + type) from a tool's
// JSON-Schema input_schema: top-level properties + one level of nested object
// properties (dotted).
export function schemaFields(input?: unknown): { path: string; type: string }[] {
  const root = input as
    | { properties?: Record<string, { type?: string; properties?: Record<string, { type?: string }> }> }
    | undefined;
  const props = root?.properties;
  if (!props || typeof props !== "object") return [];
  const out: { path: string; type: string }[] = [];
  for (const [name, spec] of Object.entries(props)) {
    out.push({ path: name, type: typeof spec?.type === "string" ? spec.type : "any" });
    if (spec?.type === "object" && spec.properties && typeof spec.properties === "object") {
      for (const [sub, ss] of Object.entries(spec.properties)) {
        out.push({ path: `${name}.${sub}`, type: typeof ss?.type === "string" ? ss.type : "any" });
      }
    }
  }
  return out;
}

export interface DraftArg {
  path: string;
  op: string;
  value: string;
}

export interface Draft {
  priority: number;
  principal: string;
  tool: string;
  action: PolicyAction;
  reason: string;
  enabled: boolean;
  args: DraftArg[];
  channels: string[]; // HOLD routing: notification channel ids (empty = all)
  ttlSecs: number; // HOLD pending window in seconds; 0 = server default
}

export function ruleToDraft(rule: PolicyRule | null): Draft {
  if (!rule) {
    return { priority: 100, principal: "", tool: "", action: "DENY", reason: "", enabled: true, args: [], channels: [], ttlSecs: 0 };
  }
  return {
    priority: rule.priority,
    principal: rule.match.principal ?? "",
    tool: rule.match.tool ?? "",
    action: rule.action,
    reason: rule.reason,
    enabled: rule.enabled,
    args: (rule.match.args ?? []).map((a) => ({ path: a.path, op: a.op, value: argValueToText(a.value) })),
    channels: rule.approval?.channels ?? [],
    ttlSecs: rule.approval?.ttl_secs ?? 0,
  };
}

// draftToInput builds the writable rule payload, dropping incomplete arg rows
// and attaching HOLD approval settings only when relevant.
export function draftToInput(d: Draft): PolicyRuleInput {
  const args: ArgCondition[] = [];
  for (const a of d.args) {
    const path = a.path.trim();
    if (path === "") continue;
    const cond: ArgCondition = { path, op: a.op };
    if (a.op !== "exists") cond.value = parseArgValue(a.value, a.op);
    args.push(cond);
  }
  return {
    priority: Number.isFinite(d.priority) ? d.priority : 100,
    match: { principal: d.principal || undefined, tool: d.tool.trim() || undefined, args: args.length ? args : undefined },
    action: d.action,
    reason: d.reason.trim(),
    enabled: d.enabled,
    approval:
      d.action === "HOLD" && (d.channels.length || d.ttlSecs)
        ? {
            ...(d.channels.length ? { channels: d.channels } : {}),
            ...(d.ttlSecs ? { ttl_secs: d.ttlSecs } : {}),
          }
        : undefined,
  };
}
